@import "me-design/tokens";

/* Design-Tokens des Kundeninfosystems — seit ME-Etappe 1 (Aufgabe 4) eine ALIAS-SCHICHT.

   Die einzige Farbquelle ist die Tokendatei von me-design (oben eingebunden). Jedes
   --ki-*-Token verweist auf ein ME-Token; hell und dunkel sind deshalb identisch
   geschrieben, denn me-design schaltet selbst um: data-theme="hell|dunkel" am <html>
   (gesetzt von applyTheme) bzw. prefers-color-scheme, wenn kein Schema gewählt ist.
   Die Unternehmensfarbe setzt data-unternehmen (me|bs|pa) — me-design färbt dann
   --marke/--aktion, und --ki-accent folgt. Die Schriftstufe setzt data-schrift.

   Die tatsächlichen Farbwerte stehen gespiegelt in tokens.ts (PALETTE); test/contrast.test.ts
   löst jeden Alias gegen die ME-Datei auf und hält beides deckungsgleich. Die --ki-*-Namen
   baut Etappe 4 ab (scripts/check-tokens.mjs zählt die Verwender). */

:root {
  --ki-bg: var(--grund);
  --ki-surface: var(--flaeche);
  --ki-surface-raised: var(--feld);
  --ki-border: var(--linie);
  /* Die Kontur eines Bedienelements (Tab, Segment). */
  --ki-border-strong: var(--linie-stark);
  --ki-text: var(--text);
  --ki-muted: var(--text-leise);
  --ki-accent: var(--aktion);
  /* Schrift auf dem Aktionston: hell Weiß, dunkel #0b0b0b — für jedes Unternehmen, denn
     me-design hellt den Aktionston im Dunkelmodus bei allen dreien auf (9778a04). */
  --ki-accent-text: var(--aktion-schrift);
  --ki-status-ok: var(--ok);
  --ki-status-warn: var(--warnung);
  --ki-status-bad: var(--fehler);
  --ki-status-running: var(--status-laeuft);
  --ki-status-off: var(--status-aus);
  /* Die Hinterlegungen der Statusfarben: 12 % des Tons auf der Fläche. */
  --ki-status-ok-bg:      color-mix(in srgb, var(--ok) 12%, var(--flaeche));
  --ki-status-warn-bg:    color-mix(in srgb, var(--warnung) 12%, var(--flaeche));
  --ki-status-bad-bg:     color-mix(in srgb, var(--fehler) 12%, var(--flaeche));
  --ki-status-running-bg: color-mix(in srgb, var(--status-laeuft) 12%, var(--flaeche));
  --ki-status-off-bg:     color-mix(in srgb, var(--status-aus) 12%, var(--flaeche));

  /* Grundschrift: die Schriftstufe (data-schrift) setzt --schrift-basis 15/17/19/22 px.
     Dichte und Zeigergerät verschieben nur Abstände und Tap-Ziele. */
  --ki-font-size: var(--schrift-basis);
  --ki-font-ui: var(--schrift);
  --ki-row-gap: 6px;
  --ki-tap-min: 28px;

  color-scheme: light dark;

  /* ------------------------------------------------------------------------------
   * Umgebungskennzeichnung (Spec "Umgebungskennzeichnung", 07.09.2026).
   *
   * Die sechs Grundwerte --umgebung-produktion/-test/-entwicklung/-schrift/-sekundaer/
   * -fokus kommen seit ME-Etappe 1 (Aufgabe 4) aus me-design/tokens (gleiche Namen, gleiche
   * Werte). Hier stehen nur noch die Kundeninfo-eigenen Ergänzungen; auch sie tragen
   * die Namen der Spec und deshalb kein "--ki-".
   *
   * Sie stehen NUR in diesem Block und in KEINEM der beiden Dunkel-Blöcke unten, und
   * genau das ist die Zusage: Die Kopfleistenfarbe ist THEME-UNABHÄNGIG. Wäre sie je
   * Schema verschieden, sagte dasselbe System je nach Geräteeinstellung etwas anderes
   * über sich — und ein Signal, das man erst deuten muss, ist keins. Die Spec verlangt
   * dafür in ihrem eigenen Design-System dieselben Werte in allen drei Theme-Blöcken;
   * hier ist die Kaskade der kürzere Weg zum selben Ergebnis: Was in den Dunkel-Blöcken
   * nicht steht, kann dort auch nicht auseinanderlaufen. Der Test in
   * test/umgebung.test.ts hält beides fest.
   *
   * Die dunkle Schrift der hellen Testwelt (Schrift, Sekundärton, Fokus) setzt seit
   * ME-Etappe 1 (Aufgabe 6) das Element me-kopfzeile selbst; die früheren
   * --umgebung-*-test-Schrifttokens sind entfallen.
   * ------------------------------------------------------------------------------ */

  /* Die drei Auflagen der Kopfleiste — die Pille unter dem aktiven Bereich, der Schweif
   * unter einem berührten Bereich und die Fläche eines aufgeklappten Werkzeug-Knopfes.
   * Sie sind AUFHELLUNGEN des jeweiligen Untergrunds und deshalb je Welt verschieden:
   * Auf den beiden dunklen Welten ein Weissanteil, auf der hellen Testwelt (Bernstein)
   * ein Schwarzanteil — Weiss auf Bernstein wäre keine Hervorhebung, sondern ein Fleck.
   * Werte aus dem Kopfleisten-Schaufenster des Design-Systems. */
  --umgebung-pille:            rgba(255, 255, 255, 0.18);
  --umgebung-schweb:           rgba(255, 255, 255, 0.10);
  --umgebung-werkzeug-schweb:  rgba(255, 255, 255, 0.16);
  --umgebung-pille-test:           rgba(0, 0, 0, 0.14);
  --umgebung-schweb-test:          rgba(0, 0, 0, 0.08);
  --umgebung-werkzeug-schweb-test: rgba(0, 0, 0, 0.08);

  /* Die 3px-Akzentlinie zeichnet seit ME-Etappe 1 (Aufgabe 6) me-kopfzeile selbst, in
   * `--marke` des Arbeitsunternehmens; --umgebung-akzent(-test) sind entfallen. */

  /* Die feste Höhe der Hauptzeile. FEST, nicht Mindesthöhe: Inhalt wird darin zentriert,
   * Schrift- und Logogrössen richten sich nach ihr und nicht umgekehrt. Die Akzentlinie
   * liegt AUSSERHALB dieser 52px. */
  --kopfhoehe: 52px;
}

/* Größenklassen: nur noch Tap-Ziele und Zeilenabstand. Die Grundschrift staffelt sich
   nicht mehr nach Breite — sie ist die Schriftstufe (data-schrift, Benutzereinstellung
   mit Gerätevorgabe, siehe schriftAus in theme/applyTheme.ts). */
@media (max-width: 743px)  { :root { --ki-tap-min: 44px; --ki-row-gap: 10px; } }
@media (min-width: 744px) and (max-width: 1279px) { :root { --ki-tap-min: 44px; --ki-row-gap: 8px; } }
@media (min-width: 1280px) { :root { --ki-tap-min: 28px; --ki-row-gap: 6px; } }

/* Zweite Achse: das Zeigergerät, nicht die Fensterbreite. Der Entwurf nennt 44 pt als
   Eigenschaft von TOUCH-Geräten — ein iPad Pro im Querformat misst aber 1366 px und fiele
   damit in die Desktop-Staffel: 28-px-Ziele auf einem reinen Touch-Gerät. "pointer:
   coarse" beschreibt genau das (Finger statt Maus) und steht bewusst NACH den
   Breitenregeln, damit es bei gleicher Spezifität gewinnt. Ein Desktop-Fenster mit Maus
   bleibt unberührt. */
@media (pointer: coarse) {
  :root { --ki-tap-min: 44px; --ki-row-gap: 8px; }
}

:root[data-ki-density="comfortable"] { --ki-row-gap: 12px; }

body {
  margin: 0;
  background: var(--ki-bg);
  color: var(--ki-text);
  font-family: var(--ki-font-ui);
  font-size: var(--ki-font-size);
  line-height: 1.45;
  -webkit-font-smoothing: antialiased;
}

/* Beträge und Nummern immer mit gleich breiten Ziffern. */
.ki-num { font-variant-numeric: tabular-nums; text-align: right; }
